import { API_BASE_URL } from "./config";

const RAW_MEDIA_BASE_URL = (process.env.REACT_APP_MEDIA_BASE_URL ?? "").trim();

const BACKEND_PROXY_ORIGIN = (() => {
  const raw = (process.env.REACT_APP_BACKEND_PROXY_TARGET ?? "").trim();
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
})();

function normalizeBaseURL(v: string) {
  if (!v) return "";
  return v.endsWith("/") ? v.slice(0, -1) : v;
}

function isAbsoluteUrl(v: string) {
  return /^https?:\/\//i.test(v);
}

function toUrlOrNull(value: string): URL | null {
  if (!value) return null;

  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function isSameOriginProxyMode() {
  return !RAW_MEDIA_BASE_URL && API_BASE_URL.startsWith("/");
}

function resolveRewriteBaseUrl(): URL | null {
  const mediaBaseUrl = toUrlOrNull(normalizeBaseURL(RAW_MEDIA_BASE_URL));
  if (mediaBaseUrl) return mediaBaseUrl;

  if (isSameOriginProxyMode()) return null;

  return toUrlOrNull(normalizeBaseURL(API_BASE_URL));
}

function shouldRewriteAbsoluteUrl(rawUrl: URL, mediaBaseUrl?: URL | null) {
  const host = rawUrl.hostname.toLowerCase();
  const pathname = rawUrl.pathname.toLowerCase();

  if (mediaBaseUrl && rawUrl.origin === mediaBaseUrl.origin) return false;
  if (host === "localhost" || host === "127.0.0.1" || host === "::1") return true;
  if (/^10\./.test(host) || /^192\.168\./.test(host) || /^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) return true;

  // REACT_APP_BACKEND_PROXY_TARGET が設定されている場合はそのオリジンのURLのみリライト。
  // それ以外のオリジン（CDN等）は直接フェッチさせ、誤ってプロキシ経由にしない。
  if (BACKEND_PROXY_ORIGIN) {
    return rawUrl.origin === BACKEND_PROXY_ORIGIN;
  }

  if (pathname.startsWith("/storage/") || pathname.startsWith("/uploads/") || pathname.startsWith("/img/")) return true;

  if (typeof window !== "undefined") {
    const currentHost = window.location.hostname.toLowerCase();
    if (host === currentHost) return true;
  }

  return false;
}

export function resolveBackendAssetUrl(path?: string | null): string | undefined {
  const raw = (path ?? "").trim();
  if (!raw) return undefined;

  const rewriteBaseUrl = resolveRewriteBaseUrl();
  const sameOriginProxyMode = isSameOriginProxyMode();
  if (isAbsoluteUrl(raw)) {
    try {
      const rawUrl = new URL(raw);

      if (sameOriginProxyMode && shouldRewriteAbsoluteUrl(rawUrl)) {
        return `${rawUrl.pathname}${rawUrl.search}${rawUrl.hash}`;
      }

      if (rewriteBaseUrl && shouldRewriteAbsoluteUrl(rawUrl, rewriteBaseUrl)) {
        return `${rewriteBaseUrl.origin}${rawUrl.pathname}${rawUrl.search}${rawUrl.hash}`;
      }
    } catch {
      // fall through to the original absolute URL
    }

    return raw;
  }

  if (rewriteBaseUrl) {
    const base = normalizeBaseURL(rewriteBaseUrl.toString());
    return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
  }

  // When API_BASE_URL is a same-origin proxy path such as "/api",
  // backend media should keep its original root-relative path.
  if (API_BASE_URL.startsWith("/")) {
    return raw.startsWith("/") ? raw : `/${raw}`;
  }

  const apiBaseUrl = normalizeBaseURL(API_BASE_URL);
  if (!apiBaseUrl) {
    return raw.startsWith("/") ? raw : `/${raw}`;
  }

  return raw.startsWith("/") ? `${apiBaseUrl}${raw}` : `${apiBaseUrl}/${raw}`;
}
